import { batch } from 'solid-js';
import { useFavList } from '@bz/common';

import { EndedMatchesSection } from './components/EndedMatchesSection';
import { LiveAndUpcomingMatchesSection } from './components/LiveAndUpcomingMatchesSection';
import { PageEmpty } from './components/PageEmpty';
import { RecommendedEventsSection } from './components/RecommendedEvents';

import { COMMON_LOADERS } from '~/components/Fixtures/Loaders';
import MatchFilters from '~/components/Fixtures/MatchFilters';
import { SportFilterItem } from '~/components/Fixtures/types';
import MarketModeTabs, { getSupportedMode } from '~/components/MarketModeTabs';
import { EventStatus } from '~/constants';
import { useLayoutMainSticky } from '~/hook';

export const route = {
  info: {
    login: true,
  },
} satisfies RouteDefinition;

type FavoriteGroupsBySport = Record<string, any[]>;

export function MyFavoritesContent(props: { isHomePage?: boolean } = {}) {
  // const active = useIsActive();

  // Set default leagueMode at page init to avoid unnecessary subsequent requests
  const { list, loading: listLoading, favListLeagueMode, setFavListSportId } = useFavList();

  // createEffect(() => {
  //   if (active()) {
  //     revalidate(get.UserFollowMatchList.key);
  //   }
  // });

  // Fetch the sport category list once at page init; no repeated requests afterward
  const [sports] = createResource(async () => {
    return COMMON_LOADERS.loadSportRecommend([EventStatus.LIVE_EVENTS, EventStatus.UPCOMING]);
  });

  // Keep the selected sport category in a signal so the sport ID persists across switches
  const [sportFilter, setSportFilter] = createSignal<SportFilterItem | undefined>();

  const groupBySport = createMemo<FavoriteGroupsBySport>(() => {
    const result: FavoriteGroupsBySport = {};

    const rawList = list() ?? [];
    const sportsList = sports() ?? [];
    // Build a Set of sport IDs for fast lookup
    const sportIds = new Set(sportsList.map((sport) => sport.id));

    for (const rawItem of rawList) {
      const sportId = (rawItem as any)?.sportId;
      // Keep only sport categories that exist in sports()
      if (!sportId || !sportIds.has(sportId)) continue;

      // Merge matches and finishedVOS data
      const matches = ((rawItem as any)?.matches ?? []) as any[];
      const finishedVOS = ((rawItem as any)?.finishedVOS ?? []) as any[];

      // Skip if there are no matches under this item
      if (!matches.length && !finishedVOS.length) continue;

      const item = {
        ...(rawItem as any),
        matches,
        finishedVOS,
      };

      (result[sportId] ??= []).push(item);
    }

    return result;
  });

  // Merge sport category options with sports present in favorites; count only favorited liveAndUpcoming + ended
  const filters = createMemo<SportFilterItem[]>(() => {
    const groups = groupBySport();
    const sportsList = sports() ?? [];

    return sportsList
      .map((sport) => {
        const items = groups[sport.id] ?? [];
        // Count total matches under this sport (only favorited matches and finishedVOS)
        const matchCount = items.reduce((sum, it) => {
          const matchesLen = Array.isArray(it?.matches) ? it.matches.length : 0;
          const finishedLen = Array.isArray(it?.finishedVOS) ? it.finishedVOS.length : 0;
          return sum + matchesLen + finishedLen;
        }, 0);

        // Hide the sport if it has no favorited matches
        if (!matchCount) return null;
        return { ...sport, matchCount } as SportFilterItem;
      })
      .filter(Boolean) as SportFilterItem[];
  });

  // When filters change, ensure the selected sport is still in the list; otherwise auto-select the first one
  createEffect(() => {
    const listNow = filters();

    // Don't set a default sport if there are no favorites
    if (!listNow.length) {
      batch(() => setSportFilter(undefined));
      return;
    }

    batch(() =>
      setSportFilter((prev) => {
        if (!prev) return listNow[0];
        // Keep the currently selected sport ID if it's still in the list
        const next = listNow.find((s) => s.id === prev.id);
        return next ?? listNow[0];
      })
    );
  });

  createEffect(() => {
    setFavListSportId(sportFilter()?.id);
  });

  const handleFilterChange = (filter: SportFilterItem) => {
    setSportFilter(filter);
  };

  // Create a Signal<SportFilterItem> conforming to the MatchFilters interface
  const sportFilterSignal: Signal<SportFilterItem> = [
    () => sportFilter() ?? (filters()[0] as SportFilterItem),
    (value: SportFilterItem | ((prev: SportFilterItem | undefined) => SportFilterItem)) => {
      if (typeof value === 'function') {
        const newValue = value(sportFilter() ?? (filters()[0] as SportFilterItem));
        handleFilterChange(newValue);
      } else {
        handleFilterChange(value);
      }
    },
  ] as Signal<SportFilterItem>;

  const payload = createMemo(() => {
    const sportId = sportFilter()?.id ?? '';
    return {
      sportId,
      marketMode: getSupportedMode(sportId),
      leagueMode: favListLeagueMode(),
    };
  });

  if (!props.isHomePage) {
    useLayoutMainSticky({ onDeps: () => payload().sportId, offsetTop: 60 });
  }

  // Filter live / upcoming matches from the selected sport category
  const liveAndUpcomingMatches = createMemo(() => {
    const id = sportFilter()?.id;
    const groups = groupBySport();
    const data = ((id && groups[id]) || []) as any[];

    // Extract matches from each record and generate a unique id
    const normalized = data
      .filter((item) => Array.isArray(item?.matches) && item.matches.length > 0)
      .map((item) => {
        return {
          ...item,
        };
      });

    return normalized as any[];
  });

  // Filter ended matches from the selected sport category
  const endedMatches = createMemo(() => {
    const id = sportFilter()?.id;
    const groups = groupBySport();
    const data = ((id && groups[id]) || []) as any[];

    // Extract finishedVOS from each record and generate a unique id
    const normalized = data
      .filter((item) => Array.isArray(item?.finishedVOS) && item.finishedVOS.length > 0)
      .map((item) => {
        return {
          ...item,
          // Replace matches with finishedVOS so downstream components can handle them uniformly
          matches: item.finishedVOS,
        };
      });

    return normalized as any[];
  });

  const loadSportFavoriteMatches = async () => {
    return liveAndUpcomingMatches();
  };

  const loadSportEndedFavoriteMatches = async () => {
    return endedMatches();
  };

  // Filter for ended matches; includes a refreshKey to trigger SportMain re-fetch when endedMatches changes
  const endedPayload = createMemo(() => ({
    ...payload(),
    _endedRefreshKey: endedMatches()
      .flatMap((g) => (g.matches as any[])?.map((m: any) => m.id) ?? [])
      .join(','),
  }));

  return (
    <>
      <div class={cx('flex items-center gap-2', !props.isHomePage && 'pt-6')}>
        <Show when={!props.isHomePage}>
          <IconFavorites class="size-8" />
        </Show>
        <span class="font-semibold uppercase text-neutral-main1 text-2xl leading-7">
          {props.isHomePage ? t('My Favorite') : t('My FAVOURITES')}
        </span>
      </div>

      <Show when={filters().length}>
        <div
          class={cx(
            'flex items-center justify-between bg-tab-global-tab-bg rounded-full gap-4 p-3',
            !props.isHomePage && 'sticky top-header-height z-10'
          )}
        >
          <MatchFilters
            filters={filters()}
            bind={sportFilterSignal}
            module="favorite"
            equals={(a, b) => a?.id === b?.id}
            isShowCount
          />
          <MarketModeTabs sportId={sportFilter()?.id!} />
        </div>
      </Show>
      <Suspense fallback={<BarLoading class="size-14" />}>
        {/* Use keyed to force full remount on sport category or sort mode change, preventing insertBefore errors from SolidJS Key component DOM reconciliation during rapid switches */}
        <Show when={`${sportFilter()?.id ?? ''}-${favListLeagueMode()}`} keyed>
          {(_key) => (
            <>
              <Show when={liveAndUpcomingMatches().length} fallback={<PageEmpty />}>
                <LiveAndUpcomingMatchesSection
                  filter={payload()}
                  fetcher={loadSportFavoriteMatches}
                  listLoading={listLoading}
                />
              </Show>
              <Show when={endedMatches().length} keyed>
                <EndedMatchesSection filter={endedPayload()} fetcher={loadSportEndedFavoriteMatches} />
              </Show>
              <RecommendedEventsSection
                filter={payload()}
                sportFilter={sportFilter}
                list={list}
                filters={filters}
                groupBySport={groupBySport}
                sports={() => sports() ?? []}
              />
            </>
          )}
        </Show>
      </Suspense>
    </>
  );
}

export default MyFavoritesContent;
